import { API_PATH, BASE_PATH } from './config';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    LinearProgress,
    Typography,
    CircularProgress,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Switch,
    FormControlLabel,
    Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart
} from 'recharts';

// Dark admin theme tokens
const dk = {
    bg: '#0d1117',
    card: '#161b22',
    elevated: '#21262d',
    hover: '#2d333b',
    border: 'rgba(99, 102, 241, 0.2)',
    borderHover: 'rgba(99, 102, 241, 0.4)',
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#6e7681',
    accent: '#6366f1',
    accentLight: '#818cf8',
    chartGrid: 'rgba(99, 102, 241, 0.1)',
};

const ServerMonitor = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [stats, setStats] = useState(null);
    const [period, setPeriod] = useState('hour');
    const [lastRefresh, setLastRefresh] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [openSettings, setOpenSettings] = useState(false);
    const [alertThresholds, setAlertThresholds] = useState({
        cpu: 80,
        memory: 85,
        disk: 90,
        enableAlerts: true
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [currentAlerts, setCurrentAlerts] = useState([]);

    const periods = [
        { value: '15min', label: 'Last 15 min' },
        { value: 'hour', label: 'Last Hour' },
        { value: '6hours', label: 'Last 6 Hours' },
        { value: 'day', label: 'Last Day' },
        { value: 'week', label: 'Last Week' },
        { value: 'month', label: 'Last Month' }
    ];

    const fetchMetrics = async (selectedPeriod = period) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/admin/server-metrics.php?period=${selectedPeriod}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 403) {
                setError('Access denied. This page is only accessible to administrators.');
                setLoading(false);
                return;
            }

            if (response.status === 401) {
                setError('Please log in to access this page.');
                setLoading(false);
                return;
            }

            const result = await response.json();

            if (result.success) {
                setMetrics(result.metrics);
                setStats(result.stats);
                setLastRefresh(new Date());
                setPeriod(selectedPeriod);
                
                // Check for alerts
                if (alertThresholds.enableAlerts) {
                    checkAlerts(result.metrics);
                }
            } else {
                setError(result.error || 'Failed to fetch metrics');
            }
        } catch (err) {
            setError('Failed to connect to server: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const checkAlerts = (metricsData) => {
        if (!metricsData || metricsData.length === 0) return;
        
        const latestMetric = metricsData[metricsData.length - 1];
        const alerts = [];
        
        if (latestMetric.cpu_usage > alertThresholds.cpu) {
            alerts.push(`🔴 HIGH CPU: ${latestMetric.cpu_usage.toFixed(1)}% (threshold: ${alertThresholds.cpu}%)`);
        }
        if (latestMetric.memory.used_percent > alertThresholds.memory) {
            alerts.push(`🟠 HIGH MEMORY: ${latestMetric.memory.used_percent.toFixed(1)}% (threshold: ${alertThresholds.memory}%)`);
        }
        if (latestMetric.disk.used_percent > alertThresholds.disk) {
            alerts.push(`🔴 HIGH DISK: ${latestMetric.disk.used_percent.toFixed(1)}% (threshold: ${alertThresholds.disk}%)`);
        }
        
        setCurrentAlerts(alerts);
        if (alerts.length > 0) {
            setSnackbar({ open: true, message: alerts[0], severity: 'warning' });
        }
    };

    const exportToCSV = () => {
        if (!metrics || metrics.length === 0) {
            setSnackbar({ open: true, message: 'No data to export', severity: 'info' });
            return;
        }

        const headers = ['Timestamp', 'CPU %', 'Memory %', 'Disk %', 'Load 1min', 'Load 5min', 'Load 15min', 'DB Connected', 'Processes'];
        const rows = metrics.map(m => [
            m.timestamp,
            m.cpu_usage.toFixed(2),
            m.memory.used_percent.toFixed(2),
            m.disk.used_percent.toFixed(2),
            m.load_average['1min'].toFixed(2),
            m.load_average['5min'].toFixed(2),
            m.load_average['15min'].toFixed(2),
            m.database.connected ? 'Yes' : 'No',
            m.process_count
        ]);

        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `server-metrics-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        setSnackbar({ open: true, message: `Exported ${metrics.length} metrics to CSV`, severity: 'success' });
    };

    const handleSaveSettings = () => {
        localStorage.setItem('serverMonitorAlerts', JSON.stringify(alertThresholds));
        setOpenSettings(false);
        setSnackbar({ open: true, message: 'Alert settings saved', severity: 'success' });
    };

    // Fetch metrics on mount and set up auto-refresh
    useEffect(() => {
        const saved = localStorage.getItem('serverMonitorAlerts');
        if (saved) {
            setAlertThresholds(JSON.parse(saved));
        }
        fetchMetrics('hour');
    }, []);

    useEffect(() => {
        if (!autoRefresh) return;

        const interval = setInterval(() => {
            fetchMetrics(period);
        }, 30000); // Refresh every 30 seconds

        return () => clearInterval(interval);
    }, [period, autoRefresh]);

    const handlePeriodChange = (event, newPeriod) => {
        if (newPeriod !== null) {
            fetchMetrics(newPeriod);
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString();
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const getMetricColor = (percent) => {
        if (percent < 50) return '#4CAF50'; // Green
        if (percent < 75) return '#FF9800'; // Orange
        return '#F44336'; // Red
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto', bgcolor: dk.bg, minHeight: '100vh' }}>
            {/* Alerts Display */}
            {currentAlerts.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    {currentAlerts.map((alert, idx) => (
                        <Alert key={idx} severity="warning" sx={{ mb: 1, bgcolor: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)', '& .MuiAlert-icon': { color: '#fbbf24' } }} icon={<WarningIcon />}>
                            {alert}
                        </Alert>
                    ))}
                </Box>
            )}

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: dk.textPrimary }}>
                    📊 Server Monitor
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: dk.textMuted }}>
                        {lastRefresh && `Last refresh: ${lastRefresh.toLocaleTimeString()}`}
                    </Typography>
                    <Tooltip title="Export data to CSV">
                        <IconButton onClick={exportToCSV} disabled={loading || !metrics} size="small" sx={{ color: dk.textSecondary, '&:hover': { bgcolor: dk.elevated, color: dk.textPrimary } }}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Alert settings">
                        <IconButton onClick={() => setOpenSettings(true)} size="small" sx={{ color: dk.textSecondary, '&:hover': { bgcolor: dk.elevated, color: dk.textPrimary } }}>
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh now">
                        <IconButton onClick={() => fetchMetrics(period)} disabled={loading} size="small" sx={{ color: dk.textSecondary, '&:hover': { bgcolor: dk.elevated, color: dk.textPrimary } }}>
                            {loading ? <CircularProgress size={24} sx={{ color: dk.accent }} /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Period Selection */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: dk.textSecondary }}>
                    Period:
                </Typography>
                <ToggleButtonGroup
                    value={period}
                    exclusive
                    onChange={handlePeriodChange}
                    size="small"
                    sx={{ flexWrap: 'wrap' }}
                >
                    {periods.map((p) => (
                        <ToggleButton
                            key={p.value}
                            value={p.value}
                            sx={{
                                color: dk.textSecondary,
                                borderColor: dk.border,
                                '&:hover': { bgcolor: dk.elevated, color: dk.textPrimary, borderColor: dk.borderHover },
                                '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight, borderColor: dk.accent },
                                '&.Mui-selected:hover': { bgcolor: `${dk.accent}35` },
                            }}
                        >
                            {p.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Box sx={{ ml: 'auto' }}>
                    <Tooltip title={autoRefresh ? 'Auto-refresh is ON (every 30s)' : 'Auto-refresh is OFF'}>
                        <Button
                            variant={autoRefresh ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            sx={autoRefresh
                                ? { bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' } }
                                : { color: dk.textSecondary, borderColor: dk.border, '&:hover': { borderColor: dk.borderHover, bgcolor: dk.elevated } }
                            }
                        >
                            {autoRefresh ? '🔄 Auto' : 'Manual'}
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', '& .MuiAlert-icon': { color: '#f87171' } }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && !metrics && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress sx={{ color: dk.accent }} />
                </Box>
            )}

            {/* Current Metrics Summary */}
            {metrics && metrics.length > 0 && (
                <>
                    {/* Latest metrics */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                            Current Status
                        </Typography>
                        <Grid container spacing={2}>
                            {/* CPU */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <CardContent>
                                        <Typography sx={{ color: dk.textSecondary, mb: 1, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                                            CPU Usage
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1.5 }}>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: getMetricColor(metrics[metrics.length - 1].cpu_usage) }}>
                                                {metrics[metrics.length - 1].cpu_usage.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={metrics[metrics.length - 1].cpu_usage}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: dk.elevated, '& .MuiLinearProgress-bar': { bgcolor: getMetricColor(metrics[metrics.length - 1].cpu_usage), borderRadius: 3 } }}
                                        />
                                        {stats && stats.cpu && (
                                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: dk.textMuted }}>
                                                Avg: {stats.cpu.avg.toFixed(1)}% | Max: {stats.cpu.max.toFixed(1)}%
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Memory */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <CardContent>
                                        <Typography sx={{ color: dk.textSecondary, mb: 1, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                                            Memory Usage
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1.5 }}>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: getMetricColor(metrics[metrics.length - 1].memory.used_percent) }}>
                                                {metrics[metrics.length - 1].memory.used_percent.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={metrics[metrics.length - 1].memory.used_percent}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: dk.elevated, '& .MuiLinearProgress-bar': { bgcolor: getMetricColor(metrics[metrics.length - 1].memory.used_percent), borderRadius: 3 } }}
                                        />
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: dk.textMuted }}>
                                            {metrics[metrics.length - 1].memory.used_gb.toFixed(1)} GB / {metrics[metrics.length - 1].memory.total_gb.toFixed(1)} GB
                                        </Typography>
                                        {stats && stats.memory && (
                                            <Typography variant="caption" sx={{ display: 'block', color: dk.textMuted }}>
                                                Avg: {stats.memory.avg_percent.toFixed(1)}%
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Disk */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <CardContent>
                                        <Typography sx={{ color: dk.textSecondary, mb: 1, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                                            Disk Usage
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1.5 }}>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: getMetricColor(metrics[metrics.length - 1].disk.used_percent) }}>
                                                {metrics[metrics.length - 1].disk.used_percent.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={metrics[metrics.length - 1].disk.used_percent}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: dk.elevated, '& .MuiLinearProgress-bar': { bgcolor: getMetricColor(metrics[metrics.length - 1].disk.used_percent), borderRadius: 3 } }}
                                        />
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: dk.textMuted }}>
                                            {metrics[metrics.length - 1].disk.used_gb.toFixed(1)} GB / {metrics[metrics.length - 1].disk.total_gb.toFixed(1)} GB
                                        </Typography>
                                        {stats && stats.disk && (
                                            <Typography variant="caption" sx={{ display: 'block', color: dk.textMuted }}>
                                                Avg: {stats.disk.avg_percent.toFixed(1)}%
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Load Average */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <CardContent>
                                        <Typography sx={{ color: dk.textSecondary, mb: 1, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                                            Load Average
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {[['1 min', '1min'], ['5 min', '5min'], ['15 min', '15min']].map(([label, key]) => (
                                                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" sx={{ color: dk.textMuted }}>{label}</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: dk.textPrimary }}>
                                                        {metrics[metrics.length - 1].load_average[key].toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Charts */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                            Trends
                        </Typography>
                        <Grid container spacing={2}>
                            {/* CPU Chart */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                                        CPU Usage Over Time
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={metrics}>
                                            <defs>
                                                <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.5} />
                                                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={dk.chartGrid} />
                                            <XAxis dataKey="timestamp" tickFormatter={formatTime} interval={Math.floor(metrics.length / 8)} tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <YAxis domain={[0, 100]} tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <ChartTooltip formatter={(value) => `${value.toFixed(1)}%`} labelFormatter={(label) => formatDate(label)} contentStyle={{ bgcolor: dk.elevated, border: `1px solid ${dk.border}`, borderRadius: 8, background: dk.elevated, color: dk.textPrimary }} />
                                            <Area type="monotone" dataKey="cpu_usage" stroke="#8884d8" strokeWidth={2} fillOpacity={1} fill="url(#colorCpu)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Paper>
                            </Grid>

                            {/* Memory Chart */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                                        Memory Usage Over Time
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={metrics.map(m => ({ ...m, memory_percent: m.memory.used_percent }))}>
                                            <defs>
                                                <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.5} />
                                                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={dk.chartGrid} />
                                            <XAxis dataKey="timestamp" tickFormatter={formatTime} interval={Math.floor(metrics.length / 8)} tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <YAxis domain={[0, 100]} tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <ChartTooltip formatter={(value) => `${value.toFixed(1)}%`} labelFormatter={(label) => formatDate(label)} contentStyle={{ background: dk.elevated, border: `1px solid ${dk.border}`, borderRadius: 8, color: dk.textPrimary }} />
                                            <Area type="monotone" dataKey="memory_percent" stroke="#82ca9d" strokeWidth={2} fillOpacity={1} fill="url(#colorMemory)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Paper>
                            </Grid>

                            {/* Disk Chart */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                                        Disk Usage Over Time
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={metrics.map(m => ({ ...m, disk_percent: m.disk.used_percent }))}>
                                            <defs>
                                                <linearGradient id="colorDisk" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#ffc658" stopOpacity={0.5} />
                                                    <stop offset="95%" stopColor="#ffc658" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={dk.chartGrid} />
                                            <XAxis dataKey="timestamp" tickFormatter={formatTime} interval={Math.floor(metrics.length / 8)}
                                            />
                                            <YAxis domain={[0, 100]} tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <ChartTooltip formatter={(value) => `${value.toFixed(1)}%`} labelFormatter={(label) => formatDate(label)} contentStyle={{ background: dk.elevated, border: `1px solid ${dk.border}`, borderRadius: 8, color: dk.textPrimary }} />
                                            <Area type="monotone" dataKey="disk_percent" stroke="#ffc658" strokeWidth={2} fillOpacity={1} fill="url(#colorDisk)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Paper>
                            </Grid>

                            {/* Load Average Chart */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                                        Load Average Over Time
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <ComposedChart data={metrics}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={dk.chartGrid} />
                                            <XAxis dataKey="timestamp" tickFormatter={formatTime} interval={Math.floor(metrics.length / 8)} tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <YAxis tick={{ fill: dk.textMuted, fontSize: 11 }} axisLine={{ stroke: dk.chartGrid }} tickLine={false} />
                                            <ChartTooltip formatter={(value) => value.toFixed(2)} labelFormatter={(label) => formatDate(label)} contentStyle={{ background: dk.elevated, border: `1px solid ${dk.border}`, borderRadius: 8, color: dk.textPrimary }} />
                                            <Legend wrapperStyle={{ color: dk.textSecondary }} />
                                            <Line type="monotone" dataKey="load_average.1min"  stroke="#ff7300" strokeWidth={2} name="1 min"  dot={false} />
                                            <Line type="monotone" dataKey="load_average.5min"  stroke="#82ca9d" strokeWidth={2} name="5 min"  dot={false} />
                                            <Line type="monotone" dataKey="load_average.15min" stroke="#8884d8" strokeWidth={2} name="15 min" dot={false} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Database & Process Info */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                            Database &amp; Processes
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                                        Database Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Status:</Typography>
                                            <Chip
                                                label={metrics[metrics.length - 1].database.connected ? 'Connected' : 'Disconnected'}
                                                size="small"
                                                sx={metrics[metrics.length - 1].database.connected
                                                    ? { color: '#10b981', bgcolor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 }
                                                    : { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 600 }
                                                }
                                            />
                                        </Box>
                                        {[
                                            { label: 'Threads Connected', value: metrics[metrics.length - 1].database.threads_connected },
                                            { label: 'Queries', value: metrics[metrics.length - 1].database.questions.toLocaleString() },
                                        ].map(({ label, value }) => (
                                            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" sx={{ color: dk.textSecondary }}>{label}:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: dk.textPrimary }}>{value}</Typography>
                                            </Box>
                                        ))}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Slow Queries:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: metrics[metrics.length - 1].database.slow_queries > 0 ? '#ef4444' : dk.textPrimary }}>
                                                {metrics[metrics.length - 1].database.slow_queries}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                                        System Processes
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Total Processes:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: dk.textPrimary }}>{metrics[metrics.length - 1].process_count}</Typography>
                                        </Box>
                                        {metrics[metrics.length - 1].php_fpm.total_processes > 0 && (
                                            <>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" sx={{ color: dk.textSecondary }}>PHP-FPM Active:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: dk.textPrimary }}>
                                                        {metrics[metrics.length - 1].php_fpm.active_processes}/{metrics[metrics.length - 1].php_fpm.total_processes}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" sx={{ color: dk.textSecondary }}>PHP-FPM Idle:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: dk.textPrimary }}>{metrics[metrics.length - 1].php_fpm.idle_processes}</Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Data Table */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: dk.textPrimary }}>
                            Detailed Metrics Data
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto', bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {['Time', 'CPU %', 'Memory %', 'Disk %', 'Load 1min', 'Processes'].map((h, i) => (
                                            <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={{ fontWeight: 700, bgcolor: dk.elevated, color: dk.textSecondary, borderBottom: `1px solid ${dk.border}`, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...metrics].reverse().map((metric, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: dk.hover }, '& td': { borderBottom: `1px solid ${dk.border}` } }}>
                                            <TableCell sx={{ color: dk.textSecondary }}>{formatDate(metric.timestamp)}</TableCell>
                                            <TableCell align="right" sx={{ color: dk.textPrimary }}>{metric.cpu_usage.toFixed(1)}%</TableCell>
                                            <TableCell align="right" sx={{ color: dk.textPrimary }}>{metric.memory.used_percent.toFixed(1)}%</TableCell>
                                            <TableCell align="right" sx={{ color: dk.textPrimary }}>{metric.disk.used_percent.toFixed(1)}%</TableCell>
                                            <TableCell align="right" sx={{ color: dk.textPrimary }}>{metric.load_average['1min'].toFixed(2)}</TableCell>
                                            <TableCell align="right" sx={{ color: dk.textPrimary }}>{metric.process_count}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Help Section */}
                    <Paper sx={{ p: 3, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: dk.textPrimary }}>
                            About Server Monitoring
                        </Typography>
                        {[
                            { title: 'CPU Usage', body: 'Percentage of CPU capacity currently in use. Spikes are normal during peak activity.' },
                            { title: 'Memory Usage', body: 'RAM in use divided by total available RAM. Values consistently above 80% may indicate need for optimization.' },
                            { title: 'Disk Usage', body: 'Storage space in use. Values above 85% should trigger cleanup or expansion planning.' },
                            { title: 'Load Average', body: 'Average number of processes running on the system over 1, 5, and 15 minute periods.' },
                            { title: 'Data Retention', body: 'Server metrics are collected every 5 minutes and retained for 30 days.' },
                        ].map(({ title, body }) => (
                            <Typography key={title} variant="body2" paragraph sx={{ color: dk.textSecondary, mb: 1.5 }}>
                                <Box component="span" sx={{ color: dk.accentLight, fontWeight: 600 }}>{title}:</Box>{' '}{body}
                            </Typography>
                        ))}
                    </Paper>
                </>
            )}

            {/* Settings Dialog */}
            <Dialog
                open={openSettings}
                onClose={() => setOpenSettings(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: '0 25px 50px rgba(0,0,0,0.5)' } }}
            >
                <DialogTitle sx={{ color: dk.textPrimary, borderBottom: `1px solid ${dk.border}`, fontWeight: 700 }}>Alert Settings</DialogTitle>
                <DialogContent sx={{ pt: 2, bgcolor: dk.card }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={alertThresholds.enableAlerts}
                                    onChange={(e) => setAlertThresholds({ ...alertThresholds, enableAlerts: e.target.checked })}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: dk.accent }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: dk.accent } }}
                                />
                            }
                            label={<Typography sx={{ color: dk.textPrimary }}>Enable Alerts</Typography>}
                        />
                        {[
                            { label: 'CPU Alert Threshold (%)', key: 'cpu', default: 80 },
                            { label: 'Memory Alert Threshold (%)', key: 'memory', default: 85 },
                            { label: 'Disk Alert Threshold (%)', key: 'disk', default: 90 },
                        ].map(({ label, key, default: def }) => (
                            <TextField
                                key={key}
                                label={label}
                                type="number"
                                value={alertThresholds[key]}
                                onChange={(e) => setAlertThresholds({ ...alertThresholds, [key]: parseInt(e.target.value) || def })}
                                inputProps={{ min: 10, max: 100 }}
                                fullWidth
                                disabled={!alertThresholds.enableAlerts}
                                sx={{
                                    '& .MuiInputLabel-root': { color: dk.textSecondary },
                                    '& .MuiInputLabel-root.Mui-focused': { color: dk.accentLight },
                                    '& .MuiInputBase-input': { color: dk.textPrimary },
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: dk.elevated,
                                        '& fieldset': { borderColor: dk.border },
                                        '&:hover fieldset': { borderColor: dk.borderHover },
                                        '&.Mui-focused fieldset': { borderColor: dk.accent },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ bgcolor: dk.card, borderTop: `1px solid ${dk.border}` }}>
                    <Button onClick={() => setOpenSettings(false)} sx={{ color: dk.textSecondary }}>Cancel</Button>
                    <Button onClick={handleSaveSettings} variant="contained" sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' } }}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%', bgcolor: dk.elevated, color: dk.textPrimary, border: `1px solid ${dk.border}` }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ServerMonitor;
